<script setup lang="ts">
import {
  Bookmark as BookmarkIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Fullscreen as FullscreenIcon,
  Pencil as PencilIcon,
  Pin as PinIcon,
  PinOff as PinOffIcon,
  Share2 as Share2Icon,
  Trash2 as Trash2Icon,
  WifiOff as WifiOffIcon,
} from '@lucide/vue'
import { computed } from 'vue'

import type { ActionMenuDensity, ActionMenuItem } from '@/components/ui/actionMenu'
import ActionMenuList from '@/components/ui/ActionMenuList.vue'
import { isTauri } from '@/services/platformService'

defineOptions({ name: 'CollectionActionSheetContent' })

const props = withDefaults(
  defineProps<{
    isVisible: boolean
    isBookmarked: boolean
    isPinned: boolean
    canRename: boolean
    canDelete: boolean
    showCenterOnMap?: boolean
    density?: ActionMenuDensity
  }>(),
  { density: 'comfortable' },
)

const emit = defineEmits<{
  share: []
  centerOnMap: []
  toggleVisibility: []
  unbookmark: []
  pin: []
  makeOffline: []
  rename: []
  delete: []
}>()

const isTauriApp = isTauri()

const actionItems = computed<ActionMenuItem[]>(() => {
  const items: ActionMenuItem[] = [
    {
      key: 'pin',
      label: props.isPinned ? 'Unpin collection' : 'Pin collection',
      icon: props.isPinned ? PinOffIcon : PinIcon,
      iconClass: props.isPinned ? 'text-amber-600' : undefined,
      onSelect: () => emit('pin'),
    },
    {
      key: 'toggle-visibility',
      label: props.isVisible ? 'Hide' : 'Unhide',
      icon: props.isVisible ? EyeOffIcon : EyeIcon,
      onSelect: () => emit('toggleVisibility'),
    },
    { key: 'share', label: 'Share', icon: Share2Icon, onSelect: () => emit('share') },
  ]

  if (isTauriApp) {
    items.push({
      key: 'make-offline',
      label: 'Make available offline',
      icon: WifiOffIcon,
      onSelect: () => emit('makeOffline'),
    })
  }

  if (props.showCenterOnMap) {
    items.push({
      key: 'center-on-map',
      label: 'Center on map',
      icon: FullscreenIcon,
      onSelect: () => emit('centerOnMap'),
    })
  }

  if (props.isBookmarked) {
    items.push({
      key: 'unbookmark',
      label: 'Remove bookmark',
      icon: BookmarkIcon,
      iconClass: 'fill-amber-500 text-amber-500',
      onSelect: () => emit('unbookmark'),
    })
  }

  if (props.canRename) {
    items.push({
      key: 'rename',
      label: 'Rename',
      icon: PencilIcon,
      onSelect: () => emit('rename'),
    })
  }

  if (props.canDelete) {
    items.push({
      key: 'delete',
      label: 'Delete',
      icon: Trash2Icon,
      danger: true,
      separated: true,
      onSelect: () => emit('delete'),
    })
  }

  return items
})
</script>

<template>
  <ActionMenuList :items="actionItems" :density="density" />
</template>
